/**
 * OtpInput Component
 *
 * Material Design 3.0 inspired OTP input with multiple size and color variants.
 * Fully SSR-compatible using CSS Modules + CSS Variables.
 *
 * @example Basic usage
 * ```tsx
 * <OtpInput
 *   numberOfDigits={6}
 *   size="medium"
 *   colorRole="primary"
 *   onAction={(action, value) => console.log(action, value)}
 * />
 * ```
 *
 * @example With countdown
 * ```tsx
 * <OtpInput
 *   numberOfDigits={4}
 *   size="large"
 *   colorRole="secondary"
 *   countdownTime={60}
 * />
 * ```
 */
import React, { forwardRef } from 'react';
import { cn } from '../../utils/cn';
import styles from './otp-input.module.css';
import type { OtpInputProps, OtpInputRef } from './otp-input.types';
import { useOtpInput } from '../../utils/use-otp-input.hook';
import { Dot } from '../../atoms/dot';
import { Input } from '../input';

const OtpInput = forwardRef<OtpInputRef, OtpInputProps>((props, ref) => {
    const { otp, focusedIndex, inputRefs, inputMode, handleChange, handleKeyDown, handlePaste, setFocusedIndex } =
        useOtpInput(props, ref);

    const {
        numberOfDigits = 6,
        secureTextEntry = false,
        disabled = false,
        hideCursor = false,
        size = 'medium',
        colorRole = 'primary',
        className,
        cellsContainerClassName,
        onFocus,
        onBlur,
        showIf = true,
    } = props;

    if (!showIf) return null;

    const inputIds = Array.from({ length: numberOfDigits }, (_, i) => `otp-${i}`);

    // Build container classes following Button pattern
    const containerClasses = cn(
        styles.otpInput,
        styles.container,

        // Color role
        colorRole === 'primary' && styles.colorPrimary,
        colorRole === 'secondary' && styles.colorSecondary,
        colorRole === 'destructive' && styles.colorDestructive,

        // Custom className
        className
    );

    const otpContainerClasses = cn(
        styles.containerOTP,

        // Size variants
        size === 'small' && styles.sizeSmall,
        size === 'medium' && styles.sizeMedium,
        size === 'large' && styles.sizeLarge,

        // Custom cellsContainerClassName
        cellsContainerClassName
    );

    return (
        <div className={containerClasses} data-testid="otp-input-container">
            {/* OTP inputs and countdown in same row */}
            <div className={styles.rowContainer}>
                <div className={otpContainerClasses}>
                    {inputIds.map((id, index) => {
                        const isFocused = focusedIndex === index;
                        const hasValue = Boolean(otp[index]);

                        return (
                            <div key={id} className={cn(styles.cell, isFocused && styles.cellFocused)}>
                                <Input
                                    id={id}
                                    ref={el => {
                                        inputRefs.current[index] = el;
                                    }}
                                    type="text"
                                    inputMode={inputMode}
                                    size="small"
                                    variant="default"
                                    value={secureTextEntry && otp[index] ? '•' : otp[index]}
                                    disabled={disabled}
                                    onChange={e => handleChange(index, e.target.value)}
                                    onKeyDown={e => handleKeyDown(index, e)}
                                    onPaste={e => {
                                        e.preventDefault();
                                        const pastedData = e.clipboardData.getData('text');
                                        if (pastedData) {
                                            handlePaste(pastedData, index);
                                        }
                                    }}
                                    onFocus={() => {
                                        setFocusedIndex(index);
                                        onFocus?.(index);
                                    }}
                                    onBlur={() => {
                                        setFocusedIndex(-1);
                                        onBlur?.(index);
                                    }}
                                    className={styles.inputWrapper}
                                    containerClassName={styles.inputContainer}
                                    inputClassName={cn(
                                        styles.inputOtp,
                                        isFocused && styles.inputFocused,
                                        disabled && styles.inputDisabled
                                    )}
                                    aria-label={`OTP digit ${index + 1}`}
                                />

                                {/* Placeholder dot when empty and not focused */}
                                {!hasValue && !isFocused && (
                                    <div className={styles.placeholder} data-testid="placeholder-dot">
                                        <Dot size="medium" variant="muted" />
                                    </div>
                                )}

                                {/* Blinking cursor when focused and empty */}
                                {isFocused && !hideCursor && !hasValue && (
                                    <div className={styles.cursorWrapper} data-testid="cursor-wrapper">
                                        <div className={styles.cursor} />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
});

OtpInput.displayName = 'OtpInput';
export default OtpInput;
